import React from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast, Toaster } from 'sonner';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider } from '@/components/theme-provider';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ExerciseProvider } from './context/ExerciseContext';
import { TimezoneProvider } from './context/TimezoneContext';
import AuthForm from './components/AuthForm';
import Layout, { useLayout } from './components/Layout';
import HeroExperience from './components/HeroExperience';
import TemplateWorkflow from './components/TemplateWorkflow';
import TodaysWorkout from './components/TodaysWorkout';
import ProgressDashboard from './components/ProgressDashboard';
import PhotoTimeline from './components/PhotoTimeline';
import PhotoUpload from './components/PhotoUpload';
import History from './components/History';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useGymCheckins } from './hooks/useGymCheckins';
import { usePostHog } from 'posthog-js/react';

// Page Components
function HeroPage({ onNotification }: { onNotification: (message: string, type: 'success' | 'error' | 'pr', exerciseData?: any) => void }) {
  const navigate = useNavigate();
  const { soundEnabled } = useLayout();

  return (
    <HeroExperience
      soundEnabled={soundEnabled}
      onCheckInComplete={() => {
        onNotification('Checked in successfully! 🎉', 'success');
        navigate('/workout');
      }}
    />
  );
}

function WorkoutPage({ onNotification }: { onNotification: (message: string, type: 'success' | 'error' | 'pr', exerciseData?: any) => void }) {
  const { isCheckedInToday, loading } = useGymCheckins();
  const navigate = useNavigate();
  const { soundEnabled } = useLayout();

  // Use useEffect for navigation to avoid React warning
  React.useEffect(() => {
    // Only redirect after loading is complete and user is definitely not checked in
    if (!loading && !isCheckedInToday()) {
      navigate('/');
    }
  }, [loading, isCheckedInToday, navigate]);

  // Show loading while checking checkin status
  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading workout...</p>
        </div>
      </div>
    );
  }

  // Don't render workout content if not checked in (will redirect via useEffect)
  if (!isCheckedInToday()) {
    return null;
  }

  return (
    <div className="space-y-12">
      {/* Compact checked-in status */}
      <HeroExperience
        soundEnabled={soundEnabled}
        showFullHero={false}
        onCheckInComplete={() => {
          // Already checked in, do nothing or scroll to exercises
        }}
      />

      <motion.div
        data-exercise-section
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
        className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-8"
      >
        <TemplateWorkflow
          onNotification={onNotification}
          soundEnabled={soundEnabled}
        />

        <TodaysWorkout />
      </motion.div>
    </div>
  );
}

function ProgressPage() {
  return <ProgressDashboard />;
}

function HistoryPage() {
  return <History />;
}

function PhotosPage({ onNotification }: { onNotification: (message: string, type: 'success' | 'error' | 'pr', exerciseData?: any) => void }) {
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Upload Progress Photo</CardTitle>
          <CardDescription>Document your fitness journey with photos</CardDescription>
        </CardHeader>
        <CardContent>
          <PhotoUpload
            enableAutoUpload={true}
            onUploadSuccess={() => {
              onNotification('Photo uploaded successfully!', 'success');
              // Refresh photo timeline after successful upload
              window.dispatchEvent(new CustomEvent('photoUploaded'));
            }}
            onUploadError={(error) => {
              onNotification(error, 'error');
            }}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Your Progress Timeline</CardTitle>
          <CardDescription>View your progress photos with newest first</CardDescription>
        </CardHeader>
        <CardContent>
          <PhotoTimeline />
        </CardContent>
      </Card>
    </div>
  );
}

function AppContent() {
  const { user, loading, isGuestMode } = useAuth();
  const { loading: checkinsLoading } = useGymCheckins();
  const posthog = usePostHog();

  // Track session start when app loads
  React.useEffect(() => {
    posthog.capture('session_start');
  }, []); // Empty dependency array = runs once on mount

  const showNotification = (message: string, type: 'success' | 'error' | 'pr', _exerciseData?: any) => {
    switch (type) {
      case 'success':
        toast.success(message);
        break;
      case 'error':
        toast.error(message);
        break;
      case 'pr':
        toast.success(message, {
          duration: 3000,
          description: '🎉 New personal record!',
        });
        break;
    }
  };

  // Allow access if user is authenticated OR in guest mode
  if (!user && !isGuestMode) {
    return <AuthForm />;
  }

  return (
    <ExerciseProvider>
      <Layout loading={loading || checkinsLoading}>
        <Routes>
          <Route path="/" element={<HeroPage onNotification={showNotification} />} />
          <Route path="/workout" element={<WorkoutPage onNotification={showNotification} />} />
          <Route path="/progress" element={<ProgressPage />} />
          <Route path="/history" element={<HistoryPage />} />
          <Route path="/photos" element={<PhotosPage onNotification={showNotification} />} />
        </Routes>
      </Layout>
    </ExerciseProvider>
  );
}

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: (failureCount, error) => {
        // Don't retry on authentication errors (401, 403)
        if (error?.message?.includes('401') || error?.message?.includes('403')) {
          return false;
        }
        // Don't retry on client errors (4xx)
        if (error?.message?.includes('4')) {
          return false;
        }
        // Retry up to 2 times for network/server errors
        return failureCount < 2;
      },
      refetchOnWindowFocus: false, // Prevent excessive refetching on window focus
      refetchOnReconnect: false, // Prevent refetch when network reconnects
      refetchOnMount: true, // Refetch when component mounts
    },
    mutations: {
      retry: (failureCount, error) => {
        // Don't retry mutations on authentication errors
        if (error?.message?.includes('401') || error?.message?.includes('403')) {
          return false;
        }
        // Retry mutations once for network errors
        return failureCount < 1;
      },
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider
        attribute="class"
        defaultTheme="system"
        enableSystem
        disableTransitionOnChange
      >
        <TimezoneProvider>
          <AuthProvider>
            <Router>
              <AppContent />
              <Toaster />
            </Router>
          </AuthProvider>
        </TimezoneProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;